import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Collapse,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SyncIcon from '@mui/icons-material/Sync';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PartnerService from '../../../services/PartnerService';
import { getConflictOptions, toResolutionPayload } from './ConflitManage';
import { formatFieldLabel, normalizeValue } from '../../../helpers/PartnerHelper';

const formatCount = (value) => Number(value || 0).toLocaleString('fr-FR');


const renderAnalysisCard = (label, value, tone = 'default', onClick = null, active = false) => {
  const colors = {
    default: { border: '#cbd8e6', background: '#ffffff', color: '#173d61' },
    success: { border: '#bbf7d0', background: '#f0fdf4', color: '#166534' },
    warning: { border: '#d5ad3f', background: '#fffbeb', color: '#8a641b' },
    info: { border: '#cbd8e6', background: '#f6f9fc', color: '#173d61' },
  };
  const palette = colors[tone] || colors.default;

  return (
    <Box
      sx={{
        border: active ? `2px solid ${palette.color}` : `1px solid ${palette.border}`,
        backgroundColor: palette.background,
        borderRadius: 2,
        p: 1.5,
        minHeight: 86,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
        boxShadow: active ? `0 0 0 3px ${palette.border}` : 'none',
        '&:hover': onClick ? {
          borderColor: palette.color,
          transform: 'translateY(-1px)',
        } : {},
      }}
      onClick={onClick || undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      } : undefined}
    >
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ color: palette.color, fontWeight: 800, fontSize: 24, mt: 0.5 }}>
        {formatCount(value)}
      </Typography>
    </Box>
  );
};

const parseMaybeJson = (value) => {
  if (!value || typeof value !== 'string') {
    return value || {};
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return {};
  }
};

const ENTITY_LABELS = {
  PARTNER: 'Partenaire',
  MARKET: 'Marche',
  STAFF: 'Personnel',
};

const CONFLICT_LABELS = {
  PARTNER_NIF_STAT_DIFF: 'NIF ou STAT différent des données PGI',
  PARTNER_IDENTITY_AMBIGUOUS: 'NIF et STAT correspondent à des partenaires différents',
  PARTNER_EMAIL_DUPLICATE: 'Email déjà utilisé par un autre partenaire',
  STAFF_CIN_AMBIGUOUS: 'CIN associé à plusieurs personnels',
  PARTNER_NIF_STAT_NAME_DIFF: 'NIF et STAT identiques, mais nom different',
  PARTNER_NAME_NIF_DIFF: 'Nom identique, mais NIF different',
  PARTNER_NAME_STAT_DIFF: 'Nom identique, mais STAT different',
  PARTNER_NAME_EXISTS: 'Partenaire deja existant',
  MARKET_NUMBER_EXISTS: 'Numero de marche deja existant',
  MARKET_NUMBER_DIFF: 'Numero de marche different',
  STAFF_NAME_CIN_DIFF: 'Nom du personnel identique, mais CIN different',
  STAFF_CIN_EXISTS: 'CIN deja existant',
  STAFF_NAME_EXISTS: 'Personnel deja existant',
};

const getEntityLabel = (entityType) => ENTITY_LABELS[entityType] || entityType || 'Conflit';

const getConflictTypeLabel = (conflictType) => (
  CONFLICT_LABELS[conflictType] || formatFieldLabel(conflictType || 'Conflit')
);

const getConflictLabel = (conflict) => {
  const data = parseMaybeJson(conflict?.conflict_data);
  const imported = data?.imported || {};
  const existing = data?.existing || {};
  const importedName = imported.NomPartenaire
    || imported.NomPersonnel
    || imported.NumeroMarche
    || imported.partenaire
    || imported.nom_personnel
    || imported.ref_num_contrat
    || imported.numero_marche_source // compatibilite staging anterieur
    || imported.raison_sociale
    || '';
  const existingName = existing.raison_sociale
    || existing.nom_complet
    || existing.nom
    || existing.numero_marche
    || existing.NumeroMarche
    || '';

  return [getConflictTypeLabel(conflict?.conflict_type), importedName && `Excel : ${importedName}`, existingName && `Base : ${existingName}`]
    .filter(Boolean)
    .join(' - ');
};

const getSavedResolutionChoice = (conflict) => {
  if (conflict.resolution !== 'USE_EXISTING_STAFF') return conflict.resolution || '';
  const data = parseMaybeJson(conflict.resolution_data);
  return data.personnel_id ? `USE_EXISTING_STAFF:${data.personnel_id}` : '';
};

const FIELD_LABELS = {
  partenaire: 'Partenaire',
  raison_sociale: 'Partenaire',
  NomPartenaire: 'Partenaire',
  nif: 'NIF',
  NIF: 'NIF',
  stat: 'STAT',
  STAT: 'STAT',
  numero_marche: 'Numero marche',
  numero_marche_source: 'Numero marche', // compatibilite staging anterieur
  ref_num_contrat: 'Numero marche',
  NumeroMarche: 'Numero marche',
  objet_contrat: 'Objet du contrat',
  ObjetContrat: 'Objet du contrat',
  nom_personnel: 'Nom personnel',
  nom_complet: 'Nom personnel',
  NomPersonnel: 'Nom personnel',
  cin: 'CIN',
  CIN: 'CIN',
  fonction: 'Fonction',
  Fonction: 'Fonction',
  consultantIndividuel: 'Consultant individuel',
  consultant_individuel: 'Consultant individuel',
  isConsultantIndividuel: 'Consultant individuel',
  is_consultant_individuel: 'Consultant individuel',
};

const isBeneficiaryField = (key) => {
  const normalized = String(key || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  return normalized.includes('beneficiaire')
    || normalized.includes('beneficiary')
    || normalized.includes('menage')
    || normalized.includes('menages');
};

const isConsultantIndividuelField = (key) => (
  String(key || '').replace(/_/g, '').toLowerCase().includes('consultantindividuel')
);

const isMissingMarketNumberField = (key) => {
  const normalized = String(key || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();

  return normalized === 'hasmissingmarchenumber'
    || normalized === 'hasmissingmarketnumber'
    || (normalized.includes('hasmissing') && normalized.includes('number') && (
      normalized.includes('marche') || normalized.includes('market')
    ));
};

const formatConflictValue = (key, value) => {
  if (isConsultantIndividuelField(key)) {
    const normalized = String(value ?? '').trim().toLowerCase();
    return value === true || normalized === 'true' || normalized === '1' || normalized === 'oui'
      ? 'Oui'
      : 'Non';
  }

  const text = normalizeValue(value);
  return text === '' ? '-' : text;
};

const getFieldLabel = (key) => FIELD_LABELS[key] || formatFieldLabel(key);

const getConflictPayload = (conflict) => {
  const data = parseMaybeJson(conflict?.conflict_data);
  return {
    imported: data?.imported && typeof data.imported === 'object' ? data.imported : {},
    existing: data?.existing && typeof data.existing === 'object' ? data.existing : {},
  };
};

const toDisplayEntries = (data) => (
  Object.entries(data || {})
    .filter(([key, value]) => (
      !isBeneficiaryField(key)
      && !isMissingMarketNumberField(key)
      && normalizeValue(value) !== ''
    ))
    .slice(0, 18)
);

const renderConflictLineTable = (title, subtitle, data, crossed = false) => {
  const entries = toDisplayEntries(data);

  return (
    <Box
      sx={{
        border: '1px solid #dbe4ef',
        borderRadius: 2,
        p: 1.25,
        backgroundColor: crossed ? '#f8fafc' : '#ffffff',
        opacity: crossed ? 0.58 : 1,
        textDecoration: crossed ? 'line-through' : 'none',
      }}
    >
      <Typography sx={{ fontWeight: 800, color: '#1f2a44' }}>{title}</Typography>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1 }}>{subtitle}</Typography>

      {entries.length > 0 ? (
        <TableContainer
          sx={{
            border: '1px solid #edf2f7',
            borderRadius: 1.5,
            overflowX: 'auto',
            overflowY: 'hidden',
            maxWidth: '100%',
          }}
        >
          <Table size="small" sx={{ minWidth: Math.max(entries.length * 150, 420) }}>
            <TableHead>
              <TableRow>
                {entries.map(([key]) => (
                  <TableCell
                    key={key}
                    sx={{
                      backgroundColor: '#f8fafc',
                      color: '#475569',
                      fontWeight: 800,
                      fontSize: 12,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {getFieldLabel(key)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                {entries.map(([key, value]) => (
                  <TableCell
                    key={`${key}-${String(value)}`}
                    sx={{
                      color: '#24324b',
                      fontSize: 13,
                      maxWidth: 220,
                      overflowWrap: 'anywhere',
                      verticalAlign: 'top',
                    }}
                  >
                    {formatConflictValue(key, value)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Aucune donnee disponible.
        </Typography>
      )}
    </Box>
  );
};

const getUpdatedFields = (imported, existing) => (
  Object.entries(imported || {})
    .filter(([key, value]) => (
      !isBeneficiaryField(key)
      && normalizeValue(value) !== ''
      && Object.prototype.hasOwnProperty.call(existing || {}, key)
      && normalizeValue(value) !== normalizeValue(existing?.[key])
    ))
    .slice(0, 6)
);

const renderPreviewAnalysis = (analysis, options = {}) => {
  if (!analysis) return <Alert severity="warning">Aucun bilan métier disponible.</Alert>;

  const summary = analysis.summary || {};
  const objectConflicts = Array.isArray(analysis.object_conflicts) ? analysis.object_conflicts : [];
  const alertCount = Number(options.alertCount || 0);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.25 }}>
        {renderAnalysisCard('Lignes exploitables', summary.valid_rows)}
        {renderAnalysisCard('Partenaires', summary.partners)}
        {renderAnalysisCard('Marchés', summary.markets)}
        {renderAnalysisCard('Alertes', alertCount, alertCount > 0 ? 'warning' : 'default',
          alertCount > 0 ? options.onOpenAlerts : null, options.activeHighlight === 'alerts')}
      </Box>
      {objectConflicts.length > 0 && (
        <Alert severity="warning">
          <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Numéros de marché avec plusieurs objets</Typography>
          <Stack spacing={1}>
            {objectConflicts.slice(0, 10).map((conflict) => (
              <Box key={conflict.numero_marche}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {conflict.numero_marche || 'Sans numéro'}
                </Typography>
                {(conflict.objects || []).map((objectItem, index) => (
                  <Typography key={String(conflict.numero_marche) + '-' + index}
                    variant="caption" sx={{ display: 'block', color: '#6b7280' }}>
                    {objectItem.objet || 'Objet non renseigné'} ({formatCount(objectItem.count)})
                  </Typography>
                ))}
              </Box>
            ))}
            {objectConflicts.length > 10 && (
              <Typography variant="caption" color="text.secondary">
                + {formatCount(objectConflicts.length - 10)} autre(s) numéro(s)
              </Typography>
            )}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
};

const RowsPreview = (props) => {
    const {
      previewOpen,
      handleClosePreview,
        loadingPreview,
        previewErrors,
        previews = [],
        activePreview,
        activeSheet,
        highlightMode,
        highlightedRows,
        activePartnerAlerts,
        handleSelectPreview,
        setActiveSheetName,
        setHighlightMode,
        handleOpenAlerts,
        previewReady,
        selectedIds,
        onSynchronize,
        submitting, setSubmitting
    } = props;
    const [feedback, setFeedback] = useState(null);
    const [conflictDecisions, setConflictDecisions] = useState({});
    const [savedConflictDecisions, setSavedConflictDecisions] = useState({});
    const [submittedConflictPeriods, setSubmittedConflictPeriods] = useState([]);
    const [conflictPage, setConflictPage] = useState(0);
    const [conflictPageSize, setConflictPageSize] = useState(10);
    const [expandedConflictId, setExpandedConflictId] = useState(null);

    useEffect(() => {
      if (!previewOpen) {
        setFeedback(null);
        setConflictDecisions({});
        setSavedConflictDecisions({});
        setSubmittedConflictPeriods([]);
        setExpandedConflictId(null);
      }
    }, [previewOpen]);

    useEffect(() => {
      setConflictPage(0);
      setExpandedConflictId(null);
    }, [activePreview?.part_period_id]);

    const getPreviewLabel = (preview) => (
  preview.part_period_label
  || preview.part_period_code
      || preview.file_name
      || 'Fichier sans libelle'
);

    const activeConflicts = useMemo(
      () => (Array.isArray(activePreview?.analysis?.conflicts)
        ? activePreview.analysis.conflicts.filter((conflict) => String(conflict?.conflict_type || '').trim() !== '')
        : []),
      [activePreview],
    );

    const visibleActiveSheetHeaders = useMemo(
      () => (Array.isArray(activeSheet?.headers)
        ? activeSheet.headers.filter((header) => !isMissingMarketNumberField(header))
        : []),
      [activeSheet],
    );

    const periodsWithDecisions = useMemo(() => previews.filter((preview) => {
      const conflicts = Array.isArray(preview?.analysis?.conflicts) ? preview.analysis.conflicts : [];
      return conflicts.some((conflict) => (
        (conflict.status === 'PENDING_CONFLICT'
          && !submittedConflictPeriods.includes(Number(preview.part_period_id)))
        || (conflictDecisions[conflict.id] || getSavedResolutionChoice(conflict))
          !== (savedConflictDecisions[conflict.id] ?? getSavedResolutionChoice(conflict))
      ));
    }), [previews, submittedConflictPeriods, conflictDecisions, savedConflictDecisions]);
    const missingConflictDecisions = periodsWithDecisions.some((preview) => (
      (preview.analysis?.conflicts || []).some((conflict) => (
        conflict.status === 'PENDING_CONFLICT' && !submittedConflictPeriods.includes(Number(preview.part_period_id))
          && !conflictDecisions[conflict.id]
      ))
    ));

    useEffect(() => {
      if (previews.length === 0) {
        return;
      }

      setConflictDecisions((previous) => {
        const next = { ...previous };
        previews.forEach((preview) => {
          const conflicts = Array.isArray(preview?.analysis?.conflicts) ? preview.analysis.conflicts : [];
          conflicts.forEach((conflict) => {
            if (next[conflict.id] === undefined) next[conflict.id] = getSavedResolutionChoice(conflict);
          });
        });
        return next;
      });
      setSavedConflictDecisions((previous) => {
        const next = { ...previous };
        previews.forEach((preview) => {
          (preview?.analysis?.conflicts || []).forEach((conflict) => {
            if (next[conflict.id] === undefined) next[conflict.id] = getSavedResolutionChoice(conflict);
          });
        });
        return next;
      });
    }, [previews]);

      const renderSyncFeedback = (feedback) => {
        if (!feedback) {
          return null;
        }
      
        return (
          <Alert severity={feedback.severity}>
            <Typography sx={{ fontWeight: 700 }}>{feedback.message}</Typography>
            {feedback.synced?.length > 0 && (
              <Typography variant="body2" sx={{ mt: 0.75 }}>
                {feedback.synced.length} periode(s) synchronisee(s).
              </Typography>
            )}
            {feedback.errors?.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                {feedback.errors.map((error, index) => (
                  <Typography key={`${error.part_period_id || 'error'}-${index}`} variant="body2">
                    {error.part_period_label || `Fichier ${error.part_period_id || index + 1}`} : {error.message || 'Erreur inconnue'}
                  </Typography>
                ))}
              </Stack>
            )}
          </Alert>
        );
      };

       const handleSynchronize = async () => {
          if (!previewReady || periodsWithDecisions.length > 0 || typeof onSynchronize !== 'function') {
            return;
          }
      
          setSubmitting(true);
          setFeedback(null);
      
          try {
            const result = await Promise.resolve(onSynchronize(selectedIds));
            const synced = result?.data?.synced || [];
            const errors = result?.data?.errors || [];
      
            setFeedback({
              severity: result?.status ? (errors.length > 0 ? 'warning' : 'success') : 'error',
              message: result?.message || 'Synchronisation terminee.',
              synced,
              errors,
            });
      
          } catch (error) {
            setFeedback({
              severity: 'error',
              message: error?.message || 'Erreur lors de la synchronisation.',
              synced: [],
              errors: [],
            });
          } finally {
            setSubmitting(false);
          }
        };

      const handleSubmitConflictResolutions = async () => {
        if (!previewReady || periodsWithDecisions.length === 0) {
          return;
        }

        setSubmitting(true);
        setFeedback(null);

        try {
          const resolvedPeriodIds = [];

          const submittedChoices = {};
          for (const preview of periodsWithDecisions) {
            const periodId = Number(preview.part_period_id);
            const conflicts = (preview?.analysis?.conflicts || []).filter((conflict) => (
              (conflict.status === 'PENDING_CONFLICT' && !submittedConflictPeriods.includes(periodId))
              || (conflictDecisions[conflict.id] || getSavedResolutionChoice(conflict))
                !== (savedConflictDecisions[conflict.id] ?? getSavedResolutionChoice(conflict))
            ));
            if (conflicts.some((conflict) => !conflictDecisions[conflict.id])) {
              throw new Error('Choisissez une résolution pour chaque conflit avant de synchroniser.');
            }
            const resolutions = conflicts.map((conflict) => ({
              id: conflict.id,
              ...toResolutionPayload(conflictDecisions[conflict.id]),
            }));

            const resolveResult = await PartnerService.resolveStaging(periodId, resolutions);
            if (!resolveResult?.status) {
              throw new Error(resolveResult?.message || 'Les resolutions n ont pas pu etre enregistrees.');
            }

            const submitResult = await PartnerService.submitStaging(periodId);
            if (!submitResult?.status) {
              throw new Error(submitResult?.message || 'Le lot n a pas pu etre soumis.');
            }

            resolvedPeriodIds.push(periodId);
            conflicts.forEach((conflict) => {
              submittedChoices[conflict.id] = conflictDecisions[conflict.id];
            });
          }

          setSavedConflictDecisions((previous) => ({ ...previous, ...submittedChoices }));
          setSubmittedConflictPeriods((previous) => (
            Array.from(new Set(previous.concat(resolvedPeriodIds)))
          ));

          const syncResult = typeof onSynchronize === 'function'
            ? await Promise.resolve(onSynchronize(selectedIds))
            : null;
          const synced = syncResult?.data?.synced || [];
          const errors = syncResult?.data?.errors || [];

          setFeedback({
            severity: syncResult ? (syncResult?.status ? (errors.length > 0 ? 'warning' : 'success') : 'error') : 'success',
            message: syncResult?.message || 'Conflits resolus et synchronisation terminee.',
            synced,
            errors,
          });

        } catch (error) {
          setFeedback({
            severity: 'error',
            message: error?.message || 'Erreur lors de la resolution des conflits.',
            synced: [],
            errors: [],
          });
        } finally {
          setSubmitting(false);
        }
      };

 return ( <>
      <Dialog
        open={previewOpen}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick' && !submitting && !loadingPreview) handleClosePreview();
        }}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: '1px solid #cbd8e6',
            boxShadow: '0 24px 54px rgba(15, 23, 42, 0.22)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            backgroundColor: '#173d61',
            color: '#fff',
          }}
        >
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Bilan métier avant synchronisation
            </Typography>
          <Typography variant="body2" sx={{ color: '#d9e8f5' }}>
              Vérifiez les données et les conflits avant de confirmer la synchronisation.
            </Typography>
          </Stack>
          <IconButton onClick={handleClosePreview} size="small" disabled={submitting || loadingPreview} sx={{ color: '#fff' }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <Divider />
        <DialogContent sx={{ py: 2.5 }}>
          <Stack spacing={2}>
            <Alert severity="info">
              Les anomalies ci-dessous sont informatives et ne bloquent pas la synchronisation.
            </Alert>

            {renderSyncFeedback(feedback)}

            {loadingPreview ? (
              <Box
                sx={{
                  minHeight: 240,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Stack spacing={1.5} alignItems="center">
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Chargement du contenu des fichiers...
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <>
                {previewErrors.length > 0 && (
                  <Alert severity="error">
                    <Typography sx={{ fontWeight: 700 }}>
                      Impossible d&apos;afficher tout le contenu des fichiers selectionnes.
                    </Typography>
                    {previewErrors.map((error, index) => (
                      <Typography key={`${error.part_period_id || 'unknown'}-${index}`} variant="body2" sx={{ mt: 0.75 }}>
                        {error.part_period_label ? `${error.part_period_label} : ` : ''}
                        {error.message}
                      </Typography>
                    ))}
                  </Alert>
                )}

                {previews.length > 0 ? (
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      {previews.map((preview) => {
                        const isActive = Number(preview.part_period_id) === Number(activePreview?.part_period_id);
                        const rowCount = (preview.sheets || []).reduce(
                          (sum, sheet) => sum + Number(sheet.row_count || 0),
                          0,
                        );

                        return (
                          <Button
                            key={Number(preview.part_period_id)}
                            variant={isActive ? 'contained' : 'outlined'}
                            onClick={() => handleSelectPreview(Number(preview.part_period_id))}
                            sx={{ textTransform: 'none' }}
                          >
                            {getPreviewLabel(preview)} ({rowCount} ligne(s))
                          </Button>
                        );
                      })}
                    </Box>

                    {activePreview && (
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: '#24324b' }}>
                            {getPreviewLabel(activePreview)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {activePreview.file_name || 'Fichier inconnu'}
                          </Typography>
                        </Box>

                        {renderPreviewAnalysis(activePreview.analysis, {
                          alertCount: activePartnerAlerts.length,
                          activeHighlight: highlightMode,
                          onHighlight: setHighlightMode,
                          onOpenAlerts: handleOpenAlerts,
                        })}

                        {activeConflicts.length > 0 && (
                          <Box sx={{ border: '1px solid #cbd8e6', borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{ p: 1.5, backgroundColor: '#f6f9fc' }}>
                              <Typography sx={{ fontWeight: 700, color: '#173d61' }}>
                                Conflits : {activeConflicts.filter((conflict) => conflict.status === 'PENDING_CONFLICT').length} à résoudre. Les décisions enregistrées restent modifiables avant synchronisation.
                              </Typography>
                            </Box>
                            <TableContainer sx={{ maxHeight: 430 }}>
                              <Table stickyHeader size="small" sx={{ minWidth: 1050 }}>
                                <TableHead>
                                  <TableRow>
                                    {['Fichier', 'Feuille / ligne', 'Entité', 'Type de conflit', 'Données', 'Résolution', 'Détail'].map((label) => (
                                      <TableCell key={label} sx={{ backgroundColor: '#173d61', color: '#fff', fontWeight: 700 }}>{label}</TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {activeConflicts.slice(conflictPage * conflictPageSize, (conflictPage + 1) * conflictPageSize).map((conflict) => {
                                    const { imported, existing } = getConflictPayload(conflict);
                                    const raw = parseMaybeJson(conflict.raw_data);
                                    const choice = conflictDecisions[conflict.id] ?? getSavedResolutionChoice(conflict);
                                    const expanded = expandedConflictId === conflict.id;
                                    return (
                                      <React.Fragment key={conflict.id}>
                                        <TableRow hover>
                                          <TableCell>{activePreview.file_name || '-'}</TableCell>
                                          <TableCell>{raw._source_sheet || '-'} / {raw._source_row || '-'}</TableCell>
                                          <TableCell>{getEntityLabel(conflict.entity_type)}</TableCell>
                                          <TableCell>{getConflictTypeLabel(conflict.conflict_type)}</TableCell>
                                          <TableCell>{getConflictLabel(conflict)}</TableCell>
                                          <TableCell sx={{ minWidth: 205 }}>
                                            <FormControl size="small" fullWidth>
                                              <InputLabel>Résolution</InputLabel>
                                              <Select
                                                label="Résolution"
                                                value={choice}
                                                disabled={submitting || feedback?.synced?.length > 0}
                                                onChange={(event) => setConflictDecisions((previous) => ({
                                                  ...previous,
                                                  [conflict.id]: event.target.value,
                                                }))}
                                              >
                                                <MenuItem value="" disabled>Choisir une résolution</MenuItem>
                                                {getConflictOptions(conflict).map((option) => (
                                                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                ))}
                                              </Select>
                                            </FormControl>
                                          </TableCell>
                                          <TableCell>
                                            <IconButton
                                              size="small"
                                              aria-label={expanded ? 'Masquer les détails' : 'Voir les détails'}
                                              onClick={() => setExpandedConflictId(expanded ? null : conflict.id)}
                                            >
                                              <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />
                                            </IconButton>
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell colSpan={7} sx={{ p: 0, borderBottom: expanded ? '1px solid #cbd8e6' : 0 }}>
                                            <Collapse in={expanded} unmountOnExit>
                                              <Stack spacing={1.5} sx={{ p: 2, backgroundColor: '#f6f9fc' }}>
                                                {choice === 'APPLY_IMPORTED_IDS' && getUpdatedFields(imported, existing).map(([key, value]) => (
                                                  <Typography key={key} variant="body2">
                                                    {getFieldLabel(key)} : {formatConflictValue(key, existing?.[key])} =&gt; {formatConflictValue(key, value)}
                                                  </Typography>
                                                ))}
                                                {renderConflictLineTable('Ligne Excel', 'Données importées', imported, choice === 'KEEP_PGI_IDS')}
                                                {renderConflictLineTable('Ligne base', 'Données déjà présentes', existing, choice === 'APPLY_IMPORTED_IDS' || choice === 'IGNORE_ROW')}
                                              </Stack>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      </React.Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            <TablePagination
                              component="div"
                              count={activeConflicts.length}
                              page={conflictPage}
                              onPageChange={(_, page) => setConflictPage(page)}
                              rowsPerPage={conflictPageSize}
                              onRowsPerPageChange={(event) => {
                                setConflictPageSize(Number(event.target.value));
                                setConflictPage(0);
                              }}
                              rowsPerPageOptions={[10, 25, 50]}
                              labelRowsPerPage="Lignes par page"
                            />
                          </Box>
                        )}

                        {submittedConflictPeriods.includes(Number(activePreview.part_period_id)) && (
                          <Alert severity="success">
                            Les conflits de cette periode sont resolus et soumis pour synchronisation.
                          </Alert>
                        )}

                        {(activePreview.sheets || []).length > 1 && (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            {activePreview.sheets.map((sheet) => (
                              <Button
                                key={sheet.sheet_name}
                                variant={sheet.sheet_name === activeSheet?.sheet_name ? 'contained' : 'outlined'}
                                onClick={() => setActiveSheetName(sheet.sheet_name)}
                                size="small"
                                sx={{ textTransform: 'none' }}
                              >
                                {sheet.sheet_name} ({sheet.row_count || 0})
                              </Button>
                            ))}
                          </Box>
                        )}

                        {activeSheet ? (
                          visibleActiveSheetHeaders.length > 0 ? (
                            <TableContainer
                              sx={{
                                maxHeight: 430,
                                border: '1px solid #e5e7eb',
                                borderRadius: 2,
                              }}
                            >
                              <Table stickyHeader size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>#</TableCell>
                                    {visibleActiveSheetHeaders.map((header) => (
                                      <TableCell
                                        key={header}
                                        sx={{ fontWeight: 700, backgroundColor: '#f8fafc', whiteSpace: 'nowrap' }}
                                      >
                                        {header}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {activeSheet.rows.map((row, rowIndex) => {
                                    const isHighlighted = highlightedRows.has(`${activeSheet.sheet_name}-${rowIndex}`);

                                    return (
                                    <TableRow
                                      key={`${activeSheet.sheet_name}-${rowIndex}`}
                                      hover
                                      sx={isHighlighted ? {
                                        backgroundColor: '#fff7ed',
                                        '&:hover': {
                                          backgroundColor: '#ffedd5',
                                        },
                                        '& td': {
                                          borderBottomColor: '#fed7aa',
                                        },
                                      } : undefined}
                                    >
                                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                        {rowIndex + 1}
                                      </TableCell>
                                      {visibleActiveSheetHeaders.map((header) => (
                                        <TableCell key={`${activeSheet.sheet_name}-${rowIndex}-${header}`}>
                                          {row[header] || '-'}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Alert severity="warning">Cette feuille ne contient pas de colonnes exploitables.</Alert>
                          )
                        ) : (
                          <Alert severity="warning">Aucune feuille disponible pour ce fichier.</Alert>
                        )}
                      </Stack>
                    )}
                  </Stack>
                ) : (
                  <Alert severity="warning">Aucun contenu n a ete retourne pour les fichiers selectionnes.</Alert>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClosePreview} disabled={submitting || loadingPreview} color="inherit">
            Fermer
          </Button>
          <Button
            variant="contained"
            onClick={periodsWithDecisions.length > 0 ? handleSubmitConflictResolutions : handleSynchronize}
            disabled={submitting || !previewReady || missingConflictDecisions || (feedback?.synced?.length > 0) || typeof onSynchronize !== 'function'}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            sx={{ backgroundColor: '#173d61', '&:hover': { backgroundColor: '#102d49' }, textTransform: 'none' }}
          >
            {periodsWithDecisions.length > 0 ? 'Enregistrer les décisions et synchroniser' : 'Synchroniser'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
);
}

export default RowsPreview;
